import { Typography } from "antd"
import React, { useMemo } from "react"
import { addCurrency } from "@utils/index"
import styles from "@css/modules/ResortSections.module.scss"
import { ArrowLeft } from "@css/icons/SVG"
import { ProsCons } from "./components/ProsCons"
import Link from "next/link"
import { useMediaQuery } from "@utils/hooks/useMediaQuery"
import { Picture } from "../../Picture"
import { useRouter } from "next/navigation"
import { SlideAppear } from "../../Animation/SlideAppear"
import { filtersState } from "@utils/FiltersState"

const { Title } = Typography

const requireImage = require.context("../images/", false, /\.(png|webp)$/)

export function ResortTop({ resort, level }) {
    const internalMobile = useMediaQuery("(max-width: 768px)")
    const router = useRouter()

    const onLinkClick = (e) => {
        e.preventDefault()
        const target = e.currentTarget.href
        document.getElementById(target.split("#")[1])?.scrollIntoView({ behavior: "smooth" })
    }

    function redirectToChart(e) {
        e.preventDefault()
        router.push(`/resorts/${resort.slug}/chart` + "?back=" + location.pathname)
    }

    function redirectToResortListings(e) {
        e.preventDefault()
        const filterUrl = "/resorts/" + resort.slug + "/listings"
        filtersState.clear(filterUrl)
        filtersState.setSort({}, filterUrl)
        filtersState.savedSearch = null
        //listingsState.state = null
        router.push(e.currentTarget.href)
    }

    const deed_expiration = resort.deed_expiration
    const annual_dues_per_point = resort.annual_dues_per_point
    const { img, webp } = useMemo(() => {
        if (!resort) return {}
        try {
            const imgDesktop = requireImage(`./${resort.slug}.png`)
            const imgMobile = requireImage(`./${resort.slug}-mobile.png`)
            const imgWebpDesktop = requireImage(`./${resort.slug}.webp`)
            const imgWebpMobile = requireImage(`./${resort.slug}-mobile.webp`)
            return {
                img: internalMobile ? imgMobile.default : imgDesktop.default,
                webp: internalMobile ? imgWebpMobile.default : imgWebpDesktop.default
            }
        } catch (error) {
            console.warn(error)
            return {
                img: {},
                webp: {}
            }
        }
    }, [internalMobile])
    if (!resort) {
        return null
    }
    const title = (
        <Title level={level} className={styles.title}>
            {resort?.name}
        </Title>
    )
    return (
        <div className={styles.root}>
            <SlideAppear className={styles.titleWrapper}>
                <Picture
                    webpSrc={webp.src}
                    defaultSrc={img.src}
                    width={img.width}
                    height={img.height}
                    alt={resort.short_name}
                    className={styles.imgWrapper}
                    imgClassName={styles.topImage}
                />
                {level === 2 ? (
                    <div className={styles.titleLink}>{title}</div>
                ) : (
                    <Link href={"/resorts"} className={styles.titleLink}>
                        <ArrowLeft className={styles.arrowLeft} />
                        {title}
                    </Link>
                )}
            </SlideAppear>
            <p className={styles.description}>{resort.description}</p>
            <SlideAppear className={styles.sectionTwo} delay={2}>
                <div className={styles.leftSide}>
                    <div className={styles.overview}>
                        <div>
                            <div className={styles.overviewTitle}>Overview</div>
                            <table className={styles.list}>
                                <tbody>
                                    <tr>
                                        <td>Deed Expiration</td>
                                        <td className={styles.boldCell}>{deed_expiration}</td>
                                    </tr>
                                    <tr>
                                        <td>Annual Dues</td>
                                        <td className={styles.boldCell}>{addCurrency(annual_dues_per_point)}</td>
                                    </tr>
                                    <tr>
                                        <td>Year Built</td>
                                        <td className={styles.boldCell}>{resort.year_build}</td>
                                    </tr>
                                    {resort.valueGrade?.grade && (
                                        <tr>
                                            <td>Value Rank</td>
                                            <td className={styles.boldCell}>
                                                <Link href="#resort-financing" onClick={onLinkClick}>
                                                    {resort.valueGrade?.grade}
                                                </Link>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div>
                        <a onClick={redirectToChart} href={`/resorts/${resort.slug}/chart`} className={styles.pointChartBtn}>
                            Point chart
                        </a>
                        <a onClick={redirectToResortListings} href={"/resorts/" + resort.slug + "/listings"} className={styles.viewListingsBtn}>
                            View listings
                        </a>
                    </div>
                </div>
                <SlideAppear className={styles.prosConsContainer} delay={3}>
                    <ProsCons title="Pros" items={resort.pros || []} color="#CC78FF" />
                    <ProsCons title="Cons" items={resort.cons || []} color="#FE5A9F" />
                </SlideAppear>
            </SlideAppear>
        </div>
    )
}
